import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { onAuthStateChanged } from 'firebase/auth'
import { auth } from './firebase'
import { FacilityProvider } from './context/FacilityContext'
import PartnerNav from './components/PartnerNav'
import Logo from './components/Logo'
import { ConfirmProvider } from './components/ui'
import Login from './Login'
import VippsAuthCallback from './VippsAuthCallback'
import PartnerDashboard from './pages/PartnerDashboard'
import TournamentsPage from './pages/TournamentsPage'
import TournamentDashboardPage from './pages/TournamentDashboardPage'
import SalesPage from './pages/SalesPage'
import ActivitiesPage from './pages/ActivitiesPage'
import RentalsPage from './pages/RentalsPage'
import './App.layout.css'

const MOBILE_QUERY = '(max-width: 900px)'

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(MOBILE_QUERY).matches : false)
  useEffect(() => {
    if (!window.matchMedia) return undefined
    const mq = window.matchMedia(MOBILE_QUERY)
    const onChange = () => setMobile(mq.matches)
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange)
    return () => (mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange))
  }, [])
  return mobile
}

function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [authenticated, setAuthenticated] = useState(!!localStorage.getItem('firebaseToken'))
  const [authChecked, setAuthChecked] = useState(false)
  const isMobile = useIsMobile()
  const collapsed = sidebarCollapsed && !isMobile

  useEffect(() => {
    if (!auth) {
      setAuthChecked(true)
      return
    }
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken()
          localStorage.setItem('firebaseToken', token)
          setAuthenticated(true)
        } catch (_) {
          setAuthenticated(false)
        }
      } else if (!localStorage.getItem('firebaseToken')) {
        setAuthenticated(false)
      }
      setAuthChecked(true)
    })
    return () => unsub()
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('firebaseToken')
    localStorage.removeItem('activeFacilityId')
    if (auth) auth.signOut().catch(() => {})
    setAuthenticated(false)
  }

  // Vent til Firebase har gjenopprettet innloggingen før appen (og arena-oppslaget) starter.
  // Ellers brukes et utløpt token fra localStorage, og /api/facilities/my feiler stille.
  if (!authChecked) {
    return (
      <div className="app-loading" role="status">
        <Logo variant="mark" height={44} />
        <span className="spinner" aria-hidden="true" />
        <span>Laster…</span>
      </div>
    )
  }

  if (!authenticated) {
    return (
      <Router>
        <Routes>
          <Route
            path="/auth/vipps"
            element={<VippsAuthCallback onLoginSuccess={() => setAuthenticated(true)} />}
          />
          <Route path="*" element={<Login onLoginSuccess={() => setAuthenticated(true)} />} />
        </Routes>
      </Router>
    )
  }

  return (
    <FacilityProvider>
      <ConfirmProvider>
        <Router>
          <div className={`app-layout ${collapsed ? 'nav-collapsed' : ''}`}>
            <PartnerNav
              isCollapsed={collapsed}
              onToggle={() => setSidebarCollapsed((c) => !c)}
              onLogout={handleLogout}
            />
            <div className="main-content">
              <main className="page-content">
                <Routes>
                  <Route path="/" element={<PartnerDashboard />} />
                  <Route path="/tournaments" element={<TournamentsPage />} />
                  <Route path="/tournaments/:tournamentId" element={<TournamentDashboardPage />} />
                  <Route path="/sales" element={<SalesPage />} />
                  <Route path="/activities" element={<ActivitiesPage />} />
                  <Route path="/rentals" element={<RentalsPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>
            </div>
          </div>
        </Router>
      </ConfirmProvider>
    </FacilityProvider>
  )
}

export default App
